import { useState } from "react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Copy, ExternalLink, Play, Youtube } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const MelhoresSitesCompletoPage = () => {
  const [copied, setCopied] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState({
    title: "",
    embedId: ""
  });

  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopied(text);
    toast.success("Link copiado para a área de transferência!");
    setTimeout(() => setCopied(null), 2000);
  };

  const openVideoDialog = (title: string, embedId: string) => {
    setSelectedVideo({ title, embedId });
    setDialogOpen(true);
  };

  const categories = [
    {
      id: "geradores",
      name: "Geradores de Tags e IA",
      sites: [
        {
          name: "RapidTags",
          description: "Gerador de tags para YouTube e outras plataformas",
          url: "https://rapidtags.io/generator",
        },
        {
          name: "Abacus AI",
          description: "Plataforma de chat com IA avançada",
          url: "https://chatllm.abacus.ai/",
        },
        {
          name: "Golex AI",
          description: "Plataforma de criação com inteligência artificial",
          url: "https://golex.ai/pt-BR/webapps/gp9bx4gs6mz8f1fbolk5l",
        },
        {
          name: "DeepMind Veo",
          description: "Modelo de IA da Google DeepMind",
          url: "https://deepmind.google/models/veo/",
        },
        {
          name: "ChatGPT",
          description: "Assistente de IA da OpenAI",
          url: "https://chat.openai.com",
        },
        {
          name: "Claude AI",
          description: "Assistente de IA da Anthropic",
          url: "https://claude.ai",
        },
        {
          name: "Perplexity",
          description: "Motor de busca com IA",
          url: "https://www.perplexity.ai",
        },
      ],
    },
    {
      id: "gifs",
      name: "GIFs e Imagens",
      sites: [
        {
          name: "Tenor",
          description: "Plataforma de GIFs animados",
          url: "https://tenor.com/pt-BR/search/os-simpsons-gifs",
        },
        {
          name: "Artlist",
          description: "Ferramenta de texto para imagem com IA",
          url: "https://artlist.io/text-to-image-ai",
        },
        {
          name: "Leonardo AI",
          description: "Plataforma para geração de imagens com IA",
          url: "https://leonardo.ai",
        },
        {
          name: "Midjourney",
          description: "Gerador de imagens com IA de alta qualidade",
          url: "https://www.midjourney.com",
        },
        {
          name: "DALL-E",
          description: "Gerador de imagens da OpenAI",
          url: "https://openai.com/dall-e-3",
        },
        {
          name: "Pexels",
          description: "Fotos gratuitas de alta qualidade",
          url: "https://www.pexels.com",
        },
        {
          name: "Unsplash",
          description: "Banco de imagens gratuitas",
          url: "https://unsplash.com",
        },
      ],
    },
    {
      id: "videos",
      name: "Criação de Vídeos",
      sites: [
        {
          name: "OneImageVideo",
          description: "Transforme imagens em vídeos incríveis",
          url: "https://www.oneimagevideo.com/",
        },
        {
          name: "Vheer",
          description: "Converta imagens em vídeos animados",
          url: "https://vheer.com/app/image-to-video",
        },
        {
          name: "Canva",
          description: "Plataforma completa de design gráfico",
          url: "https://www.canva.com/",
        },
        {
          name: "CapCut",
          description: "Editor de vídeo online gratuito",
          url: "https://www.capcut.com",
        },
        {
          name: "Runway ML",
          description: "Ferramenta de edição de vídeo com IA",
          url: "https://runwayml.com",
        },
        {
          name: "Descript",
          description: "Editor de áudio e vídeo baseado em texto",
          url: "https://www.descript.com",
        },
        {
          name: "Clideo",
          description: "Ferramentas online para edição de vídeo",
          url: "https://clideo.com",
        },
      ],
    },
    {
      id: "plataformas",
      name: "Plataformas de Vídeo",
      sites: [
        {
          name: "BitChute - Canal Killer45",
          description: "Canal alternativo de vídeos",
          url: "https://old.bitchute.com/channel/jGs5mLAXFlSu/",
        },
        {
          name: "BitChute - Criar Conta",
          description: "Registre-se usando o link de referência",
          url: "https://www.bitchute.com/accounts/referral/killer45/",
        },
        {
          name: "PayChute",
          description: "Plataforma de pagamentos para criadores",
          url: "https://www.paychute.com/c/ajuda-imediata-agora",
        },
        {
          name: "Rumble - Criar Conta",
          description: "Plataforma alternativa de vídeos",
          url: "https://rumble.com/register/FARIAS/",
        },
        {
          name: "Rumble - Canal TV",
          description: "Canal de TV no Rumble",
          url: "https://rumble.com/c/c-1285082?e9s=src_v1_cbl",
        },
        {
          name: "Odysee",
          description: "Plataforma de vídeo baseada em blockchain",
          url: "https://odysee.com",
        },
        {
          name: "LBRY",
          description: "Plataforma de conteúdo descentralizada",
          url: "https://lbry.com",
        },
      ],
    },
    {
      id: "outros",
      name: "Outros Sites",
      sites: [
        {
          name: "Resultado Jogo do Bicho",
          description: "Confira os resultados do jogo do bicho",
          url: "https://resultadojogodobicho.net/rio-de-janeiro/",
        },
        {
          name: "Encurta.net",
          description: "Encurtador de links com ganhos",
          url: "https://encurta.net/ref/killer56",
        },
        {
          name: "FaucetPay",
          description: "Microcarteira para criptomoedas",
          url: "https://faucetpay.io/?r=6547",
        },
        {
          name: "Payeer",
          description: "Sistema de pagamento online",
          url: "https://payeer.com/05327110",
        },
        {
          name: "FreeBitcoin",
          description: "Ganhe bitcoin grátis a cada hora",
          url: "https://freebitco.in/?r=1641466",
        },
        {
          name: "VieFaucet",
          description: "Ganhe criptomoedas grátis",
          url: "https://viefaucet.com/?r=64bb4dd7db722a8f70680e39",
        },
      ],
    },
    {
      id: "blogs",
      name: "Blogs e Comunidades",
      sites: [
        {
          name: "Gokagen Comunidades",
          description: "Comunidade com diversos conteúdos",
          url: "https://gokagen.comunidades.net/",
        },
        {
          name: "Livraria Gokage",
          description: "Blog com resenhas de livros e conteúdos diversos",
          url: "https://livrariagokage.blogspot.com",
        },
        {
          name: "Megazone Downloads",
          description: "Site com os melhores links da internet",
          url: "https://megazonedownloads.comunidades.net",
        },
        {
          name: "100 Contos de Terror",
          description: "Coletânea de histórias de terror",
          url: "https://livrariagokage.blogspot.com/2025/04/100-contos-de-terror.html",
        },
        {
          name: "Poder Sem Limites",
          description: "Blog sobre desenvolvimento pessoal",
          url: "https://livrariagokage.blogspot.com/2025/04/poder-sem-limites.html",
        },
      ],
    },
    {
      id: "lives",
      name: "Lives Recomendadas",
      sites: [
        {
          name: "Live #1",
          description: "Conteúdo exclusivo sobre os melhores sites",
          url: "https://www.youtube.com/live/jWG98KGDWwA",
          embedId: "jWG98KGDWwA",
          isVideo: true,
        },
        {
          name: "Live #2",
          description: "Dicas e truques para usar os melhores sites",
          url: "https://www.youtube.com/live/FeaOY4v65yk",
          embedId: "FeaOY4v65yk",
          isVideo: true,
        },
        {
          name: "Vídeo Tutorial #1",
          description: "Como usar os melhores sites da internet",
          url: "https://www.youtube.com/watch?v=TpCLyrRg6wk",
          embedId: "TpCLyrRg6wk",
          isVideo: true,
        },
        {
          name: "Vídeo Tutorial #2",
          description: "Guia completo para iniciantes",
          url: "https://www.youtube.com/watch?v=frvHdwWn_FY",
          embedId: "frvHdwWn_FY",
          isVideo: true,
        },
        {
          name: "Vídeo Tutorial #3",
          description: "Segredos dos melhores sites da internet",
          url: "https://www.youtube.com/watch?v=xFur7rNtaPE",
          embedId: "xFur7rNtaPE",
          isVideo: true,
        },
        {
          name: "Short Tutorial",
          description: "Dica rápida para usar os melhores sites",
          url: "https://www.youtube.com/shorts/Jd8xNb8PND8",
          embedId: "Jd8xNb8PND8",
          isVideo: true,
        },
      ],
    },
  ];

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Header />
      <main className="container mx-auto px-4 pt-24 pb-16">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent py-2">
            Os Melhores Sites da Internet - Edição Completa
          </h1>
          <p className="text-muted-foreground mt-4 max-w-2xl mx-auto">
            Coleção completa dos melhores sites e ferramentas online para diversos fins, desde criação de conteúdo até plataformas de vídeo alternativas.
          </p>
        </div>

        <div className="mb-8">
          <Card className="border border-border overflow-hidden">
            <CardHeader className="bg-primary/10 border-b border-border">
              <CardTitle className="text-2xl">Conheça nossa coleção completa</CardTitle>
              <CardDescription>
                Tudo o que você precisa em um só lugar. Acesse o site original em:
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-6">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <a 
                  href="https://megazonedownloads.comunidades.net/os-melhores-sites-da-internet" 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="flex items-center text-primary hover:underline"
                >
                  <ExternalLink size={16} className="mr-2" />
                  Visitar o site original: megazonedownloads.comunidades.net
                </a>
                <Button 
                  variant="outline" 
                  onClick={() => handleCopy("https://megazonedownloads.comunidades.net/os-melhores-sites-da-internet")}
                  className={copied === "https://megazonedownloads.comunidades.net/os-melhores-sites-da-internet" ? "bg-primary/10" : ""}
                >
                  <Copy size={16} className="mr-2" /> 
                  {copied === "https://megazonedownloads.comunidades.net/os-melhores-sites-da-internet" ? "Copiado!" : "Copiar Link"}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>

        <Tabs defaultValue="geradores" className="w-full">
          <div className="flex justify-center mb-8 overflow-x-auto pb-2">
            <TabsList className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2">
              {categories.map((category) => (
                <TabsTrigger key={category.id} value={category.id} className="px-4 py-2">
                  {category.name}
                </TabsTrigger>
              ))}
            </TabsList>
          </div>

          {categories.map((category) => (
            <TabsContent key={category.id} value={category.id} className="mt-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {category.sites.map((site, index) => (
                  <Card key={index} className="overflow-hidden border border-border h-full flex flex-col">
                    <CardHeader className="bg-muted/50">
                      <CardTitle className="text-xl flex items-center gap-2">
                        {site.isVideo && <Youtube size={20} className="text-red-500" />}
                        {site.name}
                      </CardTitle>
                      <CardDescription>{site.description}</CardDescription>
                    </CardHeader>
                    <CardContent className="pt-6 flex-grow">
                      {site.isVideo && (
                        <div className="aspect-video bg-muted/30 rounded-md flex items-center justify-center mb-4 overflow-hidden relative group cursor-pointer"
                          onClick={() => site.embedId && openVideoDialog(site.name, site.embedId)}
                        >
                          <img 
                            src={`https://img.youtube.com/vi/${site.embedId}/mqdefault.jpg`} 
                            alt={site.name}
                            className="w-full h-full object-cover"
                          />
                          <div className="absolute inset-0 bg-black/60 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                            <Play size={48} className="text-white" />
                          </div>
                        </div>
                      )}
                    </CardContent>
                    <CardFooter className="flex items-center justify-between border-t border-border pt-4">
                      <a 
                        href={site.url} 
                        target="_blank" 
                        rel="noopener noreferrer" 
                        className="flex items-center text-primary hover:underline"
                      >
                        Visitar {site.isVideo ? "Vídeo" : "Site"} <ExternalLink size={16} className="ml-1" />
                      </a>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={() => handleCopy(site.url)}
                        className={copied === site.url ? "bg-primary/10" : ""}
                      >
                        <Copy size={16} className="mr-1" /> 
                        {copied === site.url ? "Copiado!" : "Copiar"}
                      </Button>
                    </CardFooter>
                  </Card>
                ))}
              </div>
            </TabsContent>
          ))}
        </Tabs>
      </main>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle>{selectedVideo.title}</DialogTitle>
            <DialogDescription>
              Assista ao vídeo abaixo
            </DialogDescription>
          </DialogHeader>
          <div className="aspect-video w-full">
            <iframe
              className="w-full h-full"
              src={`https://www.youtube.com/embed/${selectedVideo.embedId}`}
              title={selectedVideo.title}
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            ></iframe>
          </div>
        </DialogContent>
      </Dialog>

      <Footer />
    </div>
  );
};

export default MelhoresSitesCompletoPage;